import { useMemo } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Icon } from '@/components/ui/icon';
import { useSyncStatus } from '@/hooks/useSyncStatus';
import { HeaderStatusPill } from './HeaderStatusPill';
import type { StatusTone } from '@/lib/statusTone';
import { cn } from '@/lib/utils';

type SyncIndicatorConfig = {
  label: string;
  tone: StatusTone;
  icon: Parameters<typeof Icon>[0]['name'];
  animate?: boolean;
};

const STATUS_CONFIG: Record<string, SyncIndicatorConfig> = {
  local: {
    label: 'Solo locale',
    tone: 'neutral',
    icon: 'offline_pin',
  },
  disabled: {
    label: 'Disattivata',
    tone: 'neutral',
    icon: 'cloud_off',
  },
  offline: {
    label: 'Offline',
    tone: 'neutral',
    icon: 'cloud_off',
  },
  syncing: {
    label: 'Sincronizzazione…',
    tone: 'info',
    icon: 'sync',
    animate: true,
  },
  error: {
    label: 'Errore',
    tone: 'danger',
    icon: 'error',
  },
  online: {
    label: 'Online',
    tone: 'success',
    icon: 'cloud_done',
  },
};

const timestampFormatter = new Intl.DateTimeFormat('it-IT', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export function SyncStatusIndicator() {
  const { status, pendingCount, lastSyncAt, lastError } = useSyncStatus();

  const config = STATUS_CONFIG[status] ?? STATUS_CONFIG.online;
  const lastSyncLabel = useMemo(() => {
    if (!lastSyncAt) return '—';
    const date = new Date(lastSyncAt);
    if (Number.isNaN(date.getTime())) return '—';
    return timestampFormatter.format(date);
  }, [lastSyncAt]);

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <HeaderStatusPill
          tone={config.tone}
          icon={config.icon}
          iconClassName={cn(config.animate && 'animate-spin')}
          label={`Sync ${config.label}`}
          ariaLabel={`Stato sync: ${config.label}`}
        />
      </TooltipTrigger>
      <TooltipContent align="end" side="bottom" className="space-y-1">
        <p className="text-xs font-semibold text-text-primary">Stato: {config.label}</p>
        <p className="text-xs text-text-secondary">In sospeso: {pendingCount}</p>
        <p className="text-xs text-text-secondary">Ultimo sync: {lastSyncLabel}</p>
        <p className="text-xs text-text-secondary">Ultimo errore: {lastError?.message ?? '—'}</p>
      </TooltipContent>
    </Tooltip>
  );
}
